import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { translations, contactInfo as defaultContactInfo } from "../i18n/translations";
import { deepMerge } from "../lib/deepMerge";
import { normalizeBranding } from "@/admin/adminPayload";
import { serviceMenu } from "@/i18n/services";
import { useSiteContent } from "./SiteContentContext";

const defaultHomepageSettings = {
  showTestimonials: true,
  showWhyUs: true,
  showPortrait: true,
  showPartners: true,
  portraitScrollSeconds: 20,
  partnersScrollSeconds: 15,
};

function parseScrollSeconds(value, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return fallback;
  return Math.min(120, Math.round(n));
}

const LanguageContext = createContext(null);

const STORAGE_KEY = "sd_lang";

export function LanguageProvider({ children }) {
  const { payload } = useSiteContent();

  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "ro";
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved === "ro" || saved === "en" ? saved : "ro";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, lang);
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const t = useMemo(
    () => deepMerge(translations[lang], payload[lang] || {}),
    [lang, payload]
  );

  const homepageSettings = useMemo(() => {
    const mergedRo = deepMerge(translations.ro, payload.ro || {});
    const hs = mergedRo.homepageSettings;
    if (!hs || typeof hs !== "object") return { ...defaultHomepageSettings };
    return {
      showTestimonials: hs.showTestimonials !== false,
      showPortrait: hs.showPortrait !== false,
      showPartners: hs.showPartners !== false,
      portraitScrollSeconds: parseScrollSeconds(
        hs.portraitScrollSeconds,
        defaultHomepageSettings.portraitScrollSeconds
      ),
      partnersScrollSeconds: parseScrollSeconds(
        hs.partnersScrollSeconds,
        defaultHomepageSettings.partnersScrollSeconds
      ),
    };
  }, [payload.ro]);

  const serviceCards = useMemo(() => {
    const cards = t.serviceCards;
    if (Array.isArray(cards) && cards.length > 0) return cards;
    return serviceMenu[lang] || serviceMenu.ro;
  }, [t, lang]);

  const contactInfo = useMemo(
    () => deepMerge({ ...defaultContactInfo }, payload.contactInfo || {}),
    [payload.contactInfo]
  );

  const branding = useMemo(() => normalizeBranding(payload.branding), [payload.branding]);

  useEffect(() => {
    const href = branding.faviconUrl?.trim() || `${window.location.origin}/favicon.ico`;
    const isSvg = /\.svg($|\?)/i.test(href);
    const isIco = /\.ico($|\?)/i.test(href);
    document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]').forEach((link) => {
      link.setAttribute("href", href);
      if (isSvg) link.setAttribute("type", "image/svg+xml");
      else if (isIco) link.setAttribute("type", "image/x-icon");
      else if (/\.png($|\?)/i.test(href)) link.setAttribute("type", "image/png");
      else if (/\.webp($|\?)/i.test(href)) link.setAttribute("type", "image/webp");
      else link.removeAttribute("type");
    });
  }, [branding.faviconUrl]);

  const value = useMemo(
    () => ({
      lang,
      setLang,
      toggleLang: () => setLang((prev) => (prev === "ro" ? "en" : "ro")),
      t,
      contactInfo,
      branding,
      homepageSettings,
      serviceCards,
    }),
    [lang, t, contactInfo, branding, homepageSettings, serviceCards]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
